import { JsonSchemaItemProperty, JsonSchemaProperty } from "../../../types";

export type FieldSchemaLike = Partial<
  JsonSchemaProperty & JsonSchemaItemProperty
>;

const LONG_TEXT_KEYWORDS = [
  "description",
  "address",
  "comment",
  "note",
  "message",
  "detail",
];
const COMPACT_KEYWORDS = [
  "id",
  "company",
  "code",
  "no",
  "num",
  "count",
  "qty",
  "rate",
  "amount",
  "percent",
  "date",
  "time",
  "posting_month",
  "current_month",
  "is_posting_month_active",
];

const getPrimaryType = (type: string | string[] | undefined): string => {
  if (!type) return "";
  if (Array.isArray(type)) {
    const normalized = type.find((item) => item !== "null");
    return normalized ?? type[0] ?? "";
  }
  return type;
};

const resolveEnumFieldSize = (property: FieldSchemaLike): string | null => {
  const enumValues = property.enum;
  if (!enumValues?.length) return null;

  const maxOptionLength = enumValues.reduce<number>((maxLength, value) => {
    return Math.max(maxLength, String(value).length);
  }, 0);

  if (maxOptionLength <= 14) {
    return "field-size-compact";
  }

  return "field-size-medium";
};

export const resolveDynamicFieldStyle = ({
  propertyKey,
  property,
}: {
  propertyKey: string;
  property?: FieldSchemaLike;
}): string => {
  if (!property) return "field-size-medium";

  // Button fields and fields with button actions
  const BUTTON_FIELD_KEYS = [
    "vendor_payment_method_button",
    "vendor_company_button",
    "vendor_category_button",
    "vendor_payments_button",
    "cash_clearing_setup_button",
    "absence_code_button",
    "holiday_button",
    "payroll_field_button",
    "tardy_code_button",
    "remit_to_parent_button",
    "car_desc_model_button",
    "car_desc_make_button",
    "story_button",
    "category_description",
    "company_type",
    "timesheet_declaration_button",
  ];

  if (BUTTON_FIELD_KEYS.includes(propertyKey)) {
    return "field-size-wide";
  }

  const propertyType = getPrimaryType(property.type);
  const key = propertyKey.toLowerCase();

  // Keep typeahead fields at regular width to avoid oversized fields
  // when labels include descriptive text (e.g. "5 - Company 5").
  // Check this BEFORE array/object to handle multi-autocomplete (array + typeaheadConfig).
  // isStore fields since we display both label and value
  if (property.typeaheadConfig || property.isStore) {
    return "field-size-medium";
  }

  //  Group ID selectors use compact width to save horizontal space
  if (property.isGroupId) {
    return "field-size-compact";
  }

  // Keep all non-object arrays at regular width (enum multi-select,
  // multi-autocomplete, and flavored multi-option fields) instead of
  // stretching across the entire row.
  if (
    propertyType === "array" &&
    getPrimaryType(property.items?.type) !== "object"
  ) {
    return "field-size-medium";
  }

  if (propertyType === "array" || propertyType === "object") {
    return "field-size-full";
  }

  const enumFieldSize = resolveEnumFieldSize(property);
  if (enumFieldSize) {
    return enumFieldSize;
  }

  if (propertyType === "boolean") {
    if (COMPACT_KEYWORDS.includes(propertyKey)) {
      return "field-size-compact";
    }
    return "";
  }

  if (property.format === "dateTime") {
    return "field-size-medium";
  }

  if (
    propertyType === "integer" ||
    propertyType === "number" ||
    property.format === "date"
  ) {
    return "field-size-compact";
  }

  // Check keyword-based sizing before generic maxLength heuristics
  // so intent-based widths take priority
  if (LONG_TEXT_KEYWORDS.some((keyword) => key.includes(keyword))) {
    return "field-size-medium";
  }

  if (COMPACT_KEYWORDS.some((keyword) => key.includes(keyword))) {
    return "field-size-compact";
  }

  // Generic maxLength heuristics as fallback
  if (property.maxLength && property.maxLength <= 8) {
    return "field-size-compact";
  }

  if (property.maxLength && property.maxLength <= 24) {
    return "field-size-medium";
  }

  if (property.maxLength && property.maxLength >= 120) {
    return "field-size-wide";
  }

  return "field-size-medium";
};
